/**
 * 3D-Hologramm-Preview für ein einzelnes Schiff.
 * Eigenes Canvas mit WebGL2, lädt GLB via loadGlb.
 */
import { ShaderProgram } from '../renderer/ShaderProgram';
import { Mat4 } from '../math/Mat4';
import { Vec3 } from '../math/Vec3';
import { SHIP_TYPE_DEFS } from '../config/ShipTypes';
import { loadGlb } from '../renderer/GlbParser';
import { calcShipPreviewFit, createShipPreviewMesh } from './ShipPreviewMesh';
import { ShipHologramAnimation } from './ShipHologramAnimation';
import vertSrc from '../shaders/ship_preview.vert.glsl?raw';
import fragSrc from '../shaders/ship_hologram.frag.glsl?raw';

const CAM_DIST = 3.0;

export class ShipHologramPreview {
  private _gl: WebGL2RenderingContext;
  private _shader!: ShaderProgram;
  private _vao: WebGLVertexArrayObject | null = null;
  private _buffers: WebGLBuffer[] = [];
  private _indexCount = 0;
  private _indexType: number;
  private _texture: WebGLTexture | null = null;
  private _color: readonly [number, number, number] = [0.3, 0.85, 1];
  private _fitScale = 1;
  private _centerY = 0;
  private _ready = false;
  private _lost = false;
  private _rafId = 0;
  private _time = 0;
  private _lastFrame = 0;
  private _proj = new Mat4();
  private _view = new Mat4();
  private _model = new Mat4();
  private _eye = new Vec3(0, 0, CAM_DIST);
  private _target = new Vec3(0, 0, 0);
  private _up = new Vec3(0, 1, 0);
  private _anim = new ShipHologramAnimation();

  constructor(private _canvas: HTMLCanvasElement) {
    const gl = _canvas.getContext('webgl2', {
      alpha: true, premultipliedAlpha: false, preserveDrawingBuffer: true, powerPreference: 'default',
    });
    if (!gl) throw new Error('WebGL2 not supported for hologram preview');
    this._gl = gl;
    this._indexType = gl.UNSIGNED_SHORT;
    _canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this._lost = true; });
    _canvas.addEventListener('webglcontextrestored', () => { this._lost = false; this._initGL(); });
    this._initGL();
  }

  async setShipType(type: number): Promise<void> {
    this._anim.resetPose();
    this._anim.resetBuild();
    const def = SHIP_TYPE_DEFS[type];
    this._color = def.color;
    const mesh = await loadGlb(def.modelUrl);
    const fit = calcShipPreviewFit(mesh.positions);
    this._fitScale = fit.fitScale;
    this._centerY = fit.centerY;
    this._setupMesh(mesh);
    if (mesh.baseColorImage) await this._loadTex(mesh.baseColorImage);
    else this._texture = null;
    this._ready = true;
  }

  enterBuildPose(): Promise<void> { return this._anim.enterBuildPose(); }
  startBuildAnimation(): Promise<void> { return this._anim.startBuildAnimation(); }

  start(): void {
    if (this._rafId) return;
    this._lastFrame = performance.now();
    const loop = () => {
      this._rafId = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min((now - this._lastFrame) / 1000, 0.05);
      this._lastFrame = now;
      this._time += dt;
      this._anim.updateRotation(dt);
      this._anim.updateBuild(dt);
      this._resizeCanvas();
      this._render();
    };
    loop();
  }

  stop(): void {
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = 0; }
  }

  dispose(): void {
    this.stop();
    this._anim.resetPose();
    this._anim.resetBuild();
    const gl = this._gl;
    for (const b of this._buffers) gl.deleteBuffer(b);
    this._buffers.length = 0;
    if (this._vao) gl.deleteVertexArray(this._vao);
    this._vao = null;
    if (this._texture) gl.deleteTexture(this._texture);
    this._texture = null;
    this._shader.dispose();
  }

  private _initGL(): void {
    const gl = this._gl;
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
  }

  private _resizeCanvas(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(this._canvas.clientWidth * dpr);
    const h = Math.round(this._canvas.clientHeight * dpr);
    if (w > 0 && h > 0 && (this._canvas.width !== w || this._canvas.height !== h)) {
      this._canvas.width = w;
      this._canvas.height = h;
    }
  }

  private _setupMesh(mesh: import('../renderer/GlbParser').ParsedMesh): void {
    const gl = this._gl;
    for (const b of this._buffers) gl.deleteBuffer(b);
    this._buffers.length = 0;
    if (this._vao) gl.deleteVertexArray(this._vao);
    const result = createShipPreviewMesh(gl, mesh);
    this._vao = result.vao;
    this._indexCount = result.indexCount;
    this._indexType = result.indexType;
    this._buffers = result.buffers;
  }

  private async _loadTex(img: { bytes: Uint8Array; mimeType: string }): Promise<void> {
    const ab = img.bytes.buffer.slice(img.bytes.byteOffset, img.bytes.byteOffset + img.bytes.byteLength) as ArrayBuffer;
    const bmp = await createImageBitmap(new Blob([ab], { type: img.mimeType }));
    const gl = this._gl;
    if (this._texture) gl.deleteTexture(this._texture);
    this._texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    bmp.close();
  }

  private _render(): void {
    if (this._lost) return;
    if (!this._ready || !this._vao) return;
    const gl = this._gl;
    if (gl.isContextLost()) return;
    const w = this._canvas.width;
    const h = this._canvas.height;
    if (w <= 0 || h <= 0) return;

    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    const aspect = w / h;
    this._proj.perspective(0.8, aspect, 0.01, 50);
    this._eye.set(0, 0.3, CAM_DIST);
    this._target.set(0, 0, 0);
    this._up.set(0, 1, 0);
    this._view.lookAt(this._eye, this._target, this._up);

    const a = this._anim.rotAngle, c = Math.cos(a), s = Math.sin(a), sc = this._fitScale;
    const d = this._model.data;
    d[0] = c*sc; d[1]=0; d[2]=s*sc; d[3]=0; d[4]=0; d[5]=sc; d[6]=0; d[7]=0;
    d[8] = -s*sc; d[9]=0; d[10]=c*sc; d[11]=0; d[12]=0; d[13]=this._centerY; d[14]=0; d[15]=1;

    this._shader.use();
    this._shader.setMat4('u_projection', this._proj.data);
    this._shader.setMat4('u_view', this._view.data);
    this._shader.setMat4('u_model', this._model.data);
    this._shader.setFloat('u_time', this._time);
    this._shader.setFloat('u_fitScale', this._fitScale);
    this._shader.setFloat('u_buildProgress', this._anim.buildProgress);
    this._shader.setVec3('u_color', this._color[0], this._color[1], this._color[2]);
    this._shader.setVec3('u_viewPos', this._eye.x, this._eye.y, this._eye.z);
    if (this._texture) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._texture);
      this._shader.setInt('u_texture', 0);
      this._shader.setFloat('u_hasTexture', 1);
    } else this._shader.setFloat('u_hasTexture', 0);

    gl.bindVertexArray(this._vao);
    if (this._indexType === 0) gl.drawArrays(gl.TRIANGLES, 0, this._indexCount);
    else gl.drawElements(gl.TRIANGLES, this._indexCount, this._indexType, 0);
    gl.bindVertexArray(null);
  }
}
